import { useQuery } from "@tanstack/react-query";

/**
 * One curated changelog highlight. The CI generator emits ONLY genuine
 * highlights here — routine bugfixes, chores, deps and refactors are filtered
 * out, so an empty `entries` array is the meaningful "no notable changes"
 * signal (rendered as such by the "Was ist neu" dialog).
 */
export interface VersionChangelogEntry {
  group: "neu" | "verbessert" | "behoben";
  text: string;
}

/** Shape of the static /version.json published on the Firebase hosting origin. */
export interface VersionManifest {
  schemaVersion: number;
  /** Human-readable SemVer of the latest deploy, or "dev". */
  version: string;
  /** Full git SHA of the latest deploy (== the bundle's VITE_SENTRY_RELEASE), or "dev". */
  commit: string;
  releasedAt: string | null;
  /** Curated highlights only; empty means "no major changes this release". */
  entries: VersionChangelogEntry[];
}

/**
 * One past release in the append-only history archive (/releases.json). Same
 * fields as a {@link VersionManifest} minus `schemaVersion` — an entry with an
 * empty `entries` array is a real release that simply had no notable highlights.
 */
export interface ReleaseHistoryEntry {
  /** Human-readable SemVer, or "" if the release job could not compute one. */
  version: string;
  /** Full git SHA this release was built from. */
  commit: string;
  releasedAt: string | null;
  entries: VersionChangelogEntry[];
}

/** Shape of the static /releases.json — the full release history, newest-first. */
export interface ReleaseHistoryManifest {
  schemaVersion: number;
  releases: ReleaseHistoryEntry[];
}

/** The git SHA this running bundle was built from (baked in at build time). */
export const RUNNING_COMMIT: string = __APP_VERSION__;
/** Human-readable SemVer this running bundle was built as (or "dev"). */
export const RUNNING_VERSION: string = __APP_SEMVER__;

const POLL_INTERVAL_MS = 60_000;

async function fetchVersionManifest(): Promise<VersionManifest | null> {
  // Same-origin static asset served by Firebase Hosting — NOT a backend call,
  // so no SDK/auth/tenant header is involved. (CLAUDE.md's "No SDK Workarounds"
  // rule governs backend endpoints; /version.json is a hosting asset.)
  const res = await fetch("/version.json", { cache: "no-store" });
  if (!res.ok) return null;
  // The Firebase SPA rewrite (** -> /index.html) serves the HTML shell when the
  // file is missing; guard so we never parse index.html as JSON.
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) return null;
  return (await res.json()) as VersionManifest;
}

async function fetchReleaseHistory(): Promise<ReleaseHistoryEntry[]> {
  // Static hosting asset on the app origin (same nature as /version.json — not a
  // backend/SDK call). Served no-store, so opening the dialog right after an
  // update always shows the newest history.
  const res = await fetch("/releases.json", { cache: "no-store" });
  if (!res.ok) return [];
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) return []; // SPA rewrite -> index.html
  const data = (await res.json()) as ReleaseHistoryManifest;
  return Array.isArray(data?.releases) ? data.releases : [];
}

/**
 * Lazily fetches the full release history for the "Was ist neu" archive. Pass
 * `enabled` (typically the dialog's open state) so the potentially large history
 * is only loaded when the user actually opens it — the frequent running-version
 * poll keeps using the small /version.json instead.
 */
export function useReleaseHistory(enabled: boolean): {
  releases: ReleaseHistoryEntry[];
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: ["app-release-history"],
    queryFn: fetchReleaseHistory,
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
  return { releases: data ?? [], isLoading };
}

export interface VersionPoll {
  manifest: VersionManifest | null;
  /** A newer deploy is live and this tab is running an older bundle. */
  updateAvailable: boolean;
  /** This running bundle is the latest deployed one. */
  isCurrent: boolean;
}

/**
 * Polls the static version manifest and derives whether the running bundle is
 * outdated. Safe to call from multiple components — react-query shares the
 * single query by key. No-op signal in dev (RUNNING_COMMIT === "dev").
 */
export function useVersionPoll(): VersionPoll {
  const { data } = useQuery({
    queryKey: ["app-version-manifest"],
    queryFn: fetchVersionManifest,
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    staleTime: 0,
    retry: false,
  });

  const manifest = data ?? null;
  const remoteCommit = manifest?.commit;

  const hasRealRunningCommit =
    RUNNING_COMMIT !== "dev" && RUNNING_COMMIT.length > 0;
  const hasRealRemoteCommit = !!remoteCommit && remoteCommit !== "dev";

  const updateAvailable =
    hasRealRunningCommit && hasRealRemoteCommit && remoteCommit !== RUNNING_COMMIT;
  const isCurrent =
    hasRealRunningCommit && hasRealRemoteCommit && remoteCommit === RUNNING_COMMIT;

  return { manifest, updateAvailable, isCurrent };
}
